// =============================================================================
// FILE STATUS HEADER  (lihat /app/memory/MASTER_BLUEPRINT.md A7)
// status   : ACTIVE (Supabase migration V2 - PREVIEW ONLY)
// version  : 1.1
// updated  : 2026-06-22
// protected: NO  -> UAT-02 Fase 2: alur Request -> Dispatch. UAT-04: penerima WAJIB
//            scan QR fisik saat Terima (dialog Scan & Terima), backend validasi exact-match.
//            Kacab buat REQUEST (tanpa QR). Admin Gudang scan QR (exact-match) -> dispatch.
//            Receive tetap sesuai role/region. Dipakai Transfer & Pengiriman Store.
// =============================================================================
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StatusBadge, CategoryBadge } from "@/v2/components/statusMap";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Loader2, PackageCheck, RefreshCw, ArrowRight, FileText, FileCheck2, Truck, Ban,
  PlusCircle, ScanLine, ListChecks, Inbox, ClipboardList, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useV2Auth } from "@/v2/V2AuthContext";
import { useV2LiveRefresh } from "@/v2/V2RealtimeContext";
import { QrBatchInput } from "@/v2/components/QrBatchInput";
import { RequestLineEditor, RequestLinesSummary } from "@/v2/components/RequestLineEditor";
import { isOwner, openPdf } from "@/v2/components/helpers";
import { EmptyState, TableSkeletonRows, ListSkeleton, useDelayedLoading } from "@/v2/components/ux";
import { ListPager, loadStoredPageSize } from "@/v2/components/ListPager";

const STATUS_VARIANT = {
  REQUESTED: "outline", DRAFT: "outline", SHIPPED: "secondary",
  RECEIVED: "default", CLOSED: "default", CANCELLED: "destructive",
};
const STATUS_LABEL = {
  REQUESTED: "Diajukan", DRAFT: "Disiapkan", SHIPPED: "Dikirim",
  RECEIVED: "Diterima", CLOSED: "Ditutup", CANCELLED: "Dibatalkan",
};
const fmt = (s) => { if (!s) return null; try { return new Date(s).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }); } catch (e) { return s; } };

// 9C.4 (GAP-2): kategori unit yang melekat sejak WO/QR (MoU tak pernah ditagih; hanya info).
const CAT_LABEL = { MoU: "MoU", Jual: "Jual" };

// Satu baris detail barang (dipakai di daftar "belum diterima" / "sudah diterima").
const ItemRow = ({ it, testid }) => (
  <li className="flex items-start justify-between gap-2 py-1.5 border-b last:border-0" data-testid={testid}>
    <div className="min-w-0">
      <div className="text-xs font-medium truncate">{it.product_name || "—"}</div>
      <div className="text-[10px] text-muted-foreground font-mono truncate">{it.qr_id}</div>
    </div>
    <div className="flex flex-wrap items-center justify-end gap-1 shrink-0 max-w-[55%]">
      {it.size && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{it.size}</Badge>}
      {it.club && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{it.club}</Badge>}
      {it.category && (
        <CategoryBadge cat={it.category} className="text-[10px] px-1.5 py-0" />
      )}
    </div>
  </li>
);

// 9C.3/9C.4: panel progres penerimaan bertahap + detail barang (nama/size/klub/kategori).
// Dipakai di dialog Scan & Terima (Store) dan dialog read-only Lihat Progres (pengirim/Kacab/Owner).
const ProgressPanel = ({ progress, testid }) => {
  if (!progress) return null;
  const missing = progress.missing_items || [];
  const saved = progress.saved_items || [];
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center justify-between rounded-lg border px-3 py-2">
        <span className="text-xs">Progres penerimaan</span>
        <Badge variant={progress.complete ? "default" : "secondary"} data-testid={`${testid}-count`}>
          {progress.scanned} / {progress.dispatched_total}
          {!progress.complete && progress.dispatched_total > 0 ? ` · kurang ${missing.length}` : ""}
        </Badge>
      </div>
      {missing.length > 0 && (
        <div className="rounded-lg border border-[hsl(var(--badge-warning))]/40 bg-[hsl(var(--badge-warning))]/10 px-3 py-2" data-testid={`${testid}-missing`}>
          <div className="text-xs font-semibold text-[hsl(var(--badge-warning))] mb-1">Belum diterima ({missing.length})</div>
          <ul className="max-h-44 overflow-y-auto">
            {missing.map((it) => <ItemRow key={it.qr_id} it={it} testid={`${testid}-missing-item`} />)}
          </ul>
        </div>
      )}
      {saved.length > 0 && (
        <details className="rounded-lg border px-3 py-2" data-testid={`${testid}-saved`}>
          <summary className="text-xs font-semibold cursor-pointer text-[hsl(var(--badge-success))] select-none">
            Sudah diterima ({saved.length})
          </summary>
          <ul className="max-h-44 overflow-y-auto mt-1">
            {saved.map((it) => <ItemRow key={it.qr_id} it={it} testid={`${testid}-saved-item`} />)}
          </ul>
        </details>
      )}
    </div>
  );
};

export const RequestDispatchFlow = ({
  title, icon: Icon, testid, docType,
  listPath, createPath, dispatchPath, receivePath, cancelPath,
  progressPath = null,
  sourceLabel, targetLabel, sourceOptions, targetOptions, defaultSource, defaultTarget,
  buildRequestBody, canCreate, canDispatch, canReceive, canCancel,
  receiverLocation, allowMou = true, createLabel = "Ajukan Permintaan", prefill = null,
  emptyTitle, emptyDescription,
  sellPlanCheck = false,  // H1b (D): pra-cek "tidak dijual" HANYA utk Pengiriman Store. Default OFF -> Transfer Gudang tak terpengaruh.
}) => {
  const { me, api } = useV2Auth();
  const owner = isOwner(me?.role);
  const [source, setSource] = useState(defaultSource);
  const [target, setTarget] = useState(defaultTarget);
  const [reqLines, setReqLines] = useState([]);
  const [products, setProducts] = useState([]);
  const [docs, setDocs] = useState([]);
  // PAGINASI T1: state pager (page_size disimpan per jenis daftar di localStorage).
  const pagerKey = `docs:${testid}`;
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => loadStoredPageSize(pagerKey));
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const listTopRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [formKey, setFormKey] = useState(0);
  // UAT-05: prefill dari Permintaan Merchandize (Kacab "Lanjutkan ke Pengiriman Store")
  const [seedRows, setSeedRows] = useState(null);
  const [prefillReqId, setPrefillReqId] = useState(null);
  const [prefillInfo, setPrefillInfo] = useState(null);
  // dispatch dialog
  const [dispatchDoc, setDispatchDoc] = useState(null);
  const [scanQrs, setScanQrs] = useState([]);
  const [sellPlanWarn, setSellPlanWarn] = useState(null);  // H1b (D): daftar barang "tidak dijual" di store tujuan (peringatan, tidak memblokir)
  // UAT-04: receive dialog (penerima WAJIB scan QR fisik, exact-match vs kiriman)
  const [receiveDoc, setReceiveDoc] = useState(null);
  const [receiveQrs, setReceiveQrs] = useState([]);
  const [progress, setProgress] = useState(null);  // P4: progress penerimaan bertahap (shipment)
  const [errDetail, setErrDetail] = useState(null); // C: Diagnostic Mode ringan (detail masalah QR)
  // 9C.3 (GAP-1): dialog read-only progres penerimaan untuk PENGIRIM (Admin Gudang) / Kacab / Owner.
  const [viewProgressDoc, setViewProgressDoc] = useState(null);
  const [viewProgress, setViewProgress] = useState(null);
  const [detailDoc, setDetailDoc] = useState(null);  // Tahap 3 UX: detail dokumen read-only (klik baris/kartu)
  const staged = !!progressPath;
  const showSkeleton = useDelayedLoading(loadingDocs && docs.length === 0);

  // Tahap 3 UX: judul/subjudul empty-state (menjelaskan LANGKAH BERIKUTNYA).
  const emptyT = emptyTitle || `Belum ada ${(title || "dokumen").toLowerCase()}`;
  const emptyD = emptyDescription || (canCreate
    ? "Buat pengajuan baru pada panel di samping untuk memulai alur ini."
    : canDispatch
      ? "Dokumen akan muncul di sini setelah Kepala Cabang mengajukan permintaan — Anda tinggal scan QR untuk mengirim."
      : "Dokumen akan muncul di sini begitu dibuat dan diproses oleh peran terkait.");

  // C: tampilkan toast ringkas + simpan detail lengkap utk modal "Lihat Detail Masalah".
  const showErr = (data, fallback) => {
    const d = (typeof data?.detail === "string" && data.detail) ? data.detail : fallback;
    setErrDetail(d);
    const first = d.split("\n")[0];
    toast.error(first.slice(0, 140) + (d.includes("\n") ? " — klik 'Lihat Detail Masalah'" : ""), {
      action: d.includes("\n") ? { label: "Lihat Detail", onClick: () => setErrDetail(d) } : undefined,
    });
  };

  const loadDocs = useCallback(async () => {
    setLoadingDocs(true);
    try {
      const sep = listPath.includes("?") ? "&" : "?";
      const r = await api(`${listPath}${sep}page=${page}&page_size=${pageSize}`);
      if (r.ok) {
        const d = await r.json();
        const items = Array.isArray(d) ? d : (d.items || []);
        setDocs(items);
        setTotal(Array.isArray(d) ? items.length : (d.total || 0));
        setPages(Array.isArray(d) ? 1 : (d.pages || 1));
      } else {
        setDocs([]); setTotal(0); setPages(1);
      }
    } finally { setLoadingDocs(false); }
  }, [api, listPath, page, pageSize]);

  const goPage = (p) => {
    setPage(p);
    if (listTopRef.current) listTopRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const changePageSize = (s) => { setPageSize(s); setPage(1); };

  const loadProducts = useCallback(async () => {
    const r = await api("/api/v2/merchandise/products");
    if (r.ok) { const d = await r.json(); setProducts(d.items || []); }
  }, [api]);

  useEffect(() => { loadDocs(); }, [loadDocs]);
  useV2LiveRefresh(loadDocs);  // FASE 7B: refresh daftar dokumen saat ada mutasi relevan
  useEffect(() => { if (canCreate) loadProducts(); }, [canCreate, loadProducts]);

  // UAT-05: konsumsi prefill (sekali) saat datang dari Permintaan Merchandize.
  useEffect(() => {
    if (prefill && prefill.requestId) {
      setSeedRows(prefill.rows && prefill.rows.length ? prefill.rows : null);
      setPrefillReqId(prefill.requestId);
      setPrefillInfo(prefill);
      if (prefill.target) setTarget(prefill.target);
      setFormKey((k) => k + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [prefill]);

  const validTargets = useMemo(() => targetOptions(source), [source, targetOptions]);
  useEffect(() => {
    if (validTargets.length && !validTargets.includes(target)) setTarget(validTargets[0]);
  }, [validTargets, target]);

  const totalReqQty = reqLines.reduce((s, l) => s + Number(l.qty || 0), 0);

  const create = async () => {
    if (!reqLines.length) { toast.error("Tambahkan minimal 1 Merchandize dengan Quantity > 0"); return; }
    setBusy(true);
    try {
      const r = await api(createPath, {
        method: "POST", body: JSON.stringify(buildRequestBody({ source, target, lines: reqLines, storeRequestId: prefillReqId })) });
      const data = await r.json();
      if (!r.ok) { toast.error(typeof data.detail === "string" ? data.detail : "Gagal membuat pengajuan"); return; }
      toast.success(`Pengajuan ${data.no || ""} dibuat (${data.request_count} pcs)`
        + (prefillReqId ? " · ter-link ke Permintaan" : "") + " · menunggu Admin Gudang scan QR");
      setReqLines([]);
      setSeedRows(null); setPrefillReqId(null); setPrefillInfo(null);
      setFormKey((k) => k + 1);
      // PAGINASI T1: setelah CREATE kembali ke halaman 1 (dokumen baru ada di atas).
      if (page !== 1) setPage(1); else loadDocs();
    } finally { setBusy(false); }
  };

  const openDispatch = (doc) => { setScanQrs([]); setSellPlanWarn(null); setDispatchDoc(doc); };

  // H1b (D): pra-cek BACA "tidak dijual" di store tujuan. Advisory saja; kegagalan pra-cek
  // TIDAK menghalangi pengiriman. HANYA aktif bila sellPlanCheck (Pengiriman Store).
  const runSellPlanPrecheck = async () => {
    const store = dispatchDoc?.to_store;
    if (!sellPlanCheck || !store || !scanQrs.length) return [];
    try {
      const r = await api("/api/v2/merchandise/sell-plan-precheck", {
        method: "POST", body: JSON.stringify({ store, qr_ids: scanQrs }) });
      if (!r.ok) return [];
      const d = await r.json().catch(() => ({}));
      return d.blocked || [];
    } catch (e) { return []; }
  };

  const doDispatch = async () => {
    setActingId(dispatchDoc.id + "dispatch");
    try {
      const r = await api(dispatchPath(dispatchDoc.id), {
        method: "POST", body: JSON.stringify({ qr_ids: scanQrs }) });
      const data = await r.json();
      if (!r.ok) { showErr(data, "Gagal dispatch"); return; }
      toast.success(`Konfirmasi Kirim berhasil · ${data.planned_count} item dikirim`);
      setDispatchDoc(null); setScanQrs([]); setSellPlanWarn(null); loadDocs();
    } finally { setActingId(null); }
  };

  const submitDispatch = async () => {
    if (!dispatchDoc) return;
    if (dispatchDoc.status === "REQUESTED" && scanQrs.length === 0) {
      toast.error("Scan QR sesuai request terlebih dahulu"); return;
    }
    // Peringatan "tidak dijual" ditampilkan SEKALI; klik berikutnya (Lanjutkan) tetap mengirim.
    if (sellPlanCheck && sellPlanWarn === null) {
      const warns = await runSellPlanPrecheck();
      if (warns.length) { setSellPlanWarn(warns); return; }
    }
    await doDispatch();
  };

  const openReceive = async (doc) => {
    setReceiveQrs([]); setProgress(null); setReceiveDoc(doc);
    if (progressPath) {
      try {
        const r = await api(progressPath(doc.id));
        if (r.ok) setProgress((await r.json()).progress);
      } catch (e) { /* abaikan; tetap bisa scan */ }
    }
  };

  // 9C.3 (GAP-1): buka progres penerimaan READ-ONLY (pengirim/Kacab/Owner). Tanpa scan/write.
  const openViewProgress = async (doc) => {
    setViewProgress(null); setViewProgressDoc(doc);
    if (!progressPath) return;
    try {
      const r = await api(progressPath(doc.id));
      if (r.ok) { setViewProgress((await r.json()).progress); }
      else { toast.error("Tidak dapat memuat progres (akses/region)."); }
    } catch (e) { toast.error("Gagal memuat progres penerimaan."); }
  };

  // P4: simpan progress scan bertahap (shipment) — item belum pindah ke store.
  const saveProgress = async () => {
    if (!receiveDoc || !progressPath) return;
    if (receiveQrs.length === 0) { toast.error("Scan minimal 1 QR untuk disimpan"); return; }
    setActingId(receiveDoc.id + "progress");
    try {
      const r = await api(progressPath(receiveDoc.id), {
        method: "POST", body: JSON.stringify({ qr_ids: receiveQrs }) });
      const data = await r.json();
      if (!r.ok) { showErr(data, "Gagal simpan progress"); return; }
      setProgress(data.progress); setReceiveQrs([]);
      toast.success(data.message || "Progress tersimpan");
    } finally { setActingId(null); }
  };

  const submitReceive = async () => {
    if (!receiveDoc) return;
    const need = receiveDoc.planned_count || 0;
    // Staged (shipment): boleh kirim hanya sisa scan; backend gabung dgn progress tersimpan (wajib 100%).
    if (!staged && receiveQrs.length === 0) { toast.error("Scan QR barang yang diterima terlebih dahulu"); return; }
    setActingId(receiveDoc.id + "diterima");
    try {
      const r = await api(receivePath(receiveDoc.id), {
        method: "POST", body: JSON.stringify({ qr_ids: receiveQrs }) });
      const data = await r.json();
      if (!r.ok) { showErr(data, "Gagal Konfirmasi Terima"); return; }
      toast.success(`Konfirmasi Terima Final berhasil · Tanda Terima diterbitkan`);
      setReceiveDoc(null); setReceiveQrs([]); setProgress(null); loadDocs();
    } finally { setActingId(null); }
  };

  const act = async (doc, pathFn, verb) => {
    setActingId(doc.id + verb);
    try {
      const r = await api(pathFn(doc.id), { method: "POST", body: JSON.stringify({}) });
      const data = await r.json();
      if (!r.ok) { toast.error(typeof data.detail === "string" ? data.detail : `Gagal ${verb}`); return; }
      if (data.reversed !== undefined) toast.success(`Dibatalkan · ${data.reversed} item dikembalikan ke sumber`);
      else toast.success(`Berhasil: ${STATUS_LABEL[data.status] || verb}`);
      loadDocs();
    } finally { setActingId(null); }
  };

  // Fase 4C: aksi dipakai ulang di tabel (desktop) & card (mobile). sfx=suffix testid unik.
  const renderActions = (d, sfx = "") => {
    const to = d.to_location || d.to_store;
    const shipped = d.status === "SHIPPED" || d.status === "RECEIVED" || d.status === "CLOSED";
    return (
      <>
        {docType && shipped && (
          <Button size="sm" variant="ghost" onClick={() => openPdf(api, docType, d.id, d.no)} data-testid={`${testid}-pdf${sfx}`} title="Surat Jalan PDF">
            <FileText className="w-4 h-4" />
          </Button>
        )}
        {docType && (d.status === "RECEIVED" || d.status === "CLOSED") && (
          <Button size="sm" variant="ghost" onClick={() => openPdf(api, "tanda-terima", d.id, `TT-${d.no}`)} data-testid={`${testid}-tt${sfx}`} title="Tanda Terima PDF">
            <FileCheck2 className="w-4 h-4" />
          </Button>
        )}
        {canDispatch && d.status === "REQUESTED" && (
          <Button size="sm" onClick={() => openDispatch(d)} data-testid={`${testid}-open-dispatch${sfx}`}>
            <ScanLine className="w-4 h-4 mr-1" />Scan &amp; Kirim
          </Button>
        )}
        {canDispatch && d.status === "DRAFT" && (
          <Button size="sm" onClick={() => act(d, dispatchPath, "dispatch")} disabled={actingId === d.id + "dispatch"} data-testid={`${testid}-dispatch-legacy${sfx}`}>
            {actingId === d.id + "dispatch" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}Konfirmasi Kirim
          </Button>
        )}
        {canReceive && d.status === "SHIPPED" && (!receiverLocation || to === receiverLocation) && (
          <Button size="sm" variant="outline" onClick={() => openReceive(d)} disabled={actingId === d.id + "diterima"} data-testid={`${testid}-receive${sfx}`}>
            {actingId === d.id + "diterima" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1" />}Scan &amp; Terima
          </Button>
        )}
        {progressPath && !canReceive && (d.status === "SHIPPED" || d.status === "RECEIVED") && (
          <Button size="sm" variant="ghost" onClick={() => openViewProgress(d)} data-testid={`${testid}-view-progress${sfx}`} title="Lihat Progres Penerimaan">
            <ListChecks className="w-4 h-4 mr-1" />Progres
          </Button>
        )}
        {canCancel && (d.status === "REQUESTED" || d.status === "DRAFT" || d.status === "SHIPPED") && (
          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => act(d, cancelPath, "dibatalkan")} disabled={actingId === d.id + "dibatalkan"} data-testid={`${testid}-cancel${sfx}`} title="Batalkan">
            {actingId === d.id + "dibatalkan" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
          </Button>
        )}
      </>
    );
  };

  return (
    <div className="space-y-5" data-testid={testid}>
      <h2 className="text-2xl font-semibold flex items-center gap-2">{Icon && <Icon className="w-6 h-6" />}{title}</h2>

      <div className="grid lg:grid-cols-2 gap-4">
        {canCreate ? (
          <Card className="p-5 rounded-2xl space-y-4" data-testid={`${testid}-create-card`}>
            <div className="flex items-center gap-2"><PlusCircle className="w-4 h-4 text-primary" /><h3 className="font-semibold">{createLabel}</h3></div>
            {prefillInfo && (
              <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2 text-xs flex items-start justify-between gap-2" data-testid={`${testid}-prefill-banner`}>
                <div>
                  <b>Dari Permintaan {prefillInfo.requestNo || ""}</b> — form ini sudah diisi otomatis dari permintaan store.
                  {typeof prefillInfo.totalKurang === "number" && prefillInfo.totalKurang > 0 && (
                    <span className="text-[hsl(var(--badge-warning))]"> Stok kurang {prefillInfo.totalKurang} pcs — Anda boleh revisi qty manual sebelum Ajukan.</span>
                  )}
                </div>
                <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => { setPrefillInfo(null); setPrefillReqId(null); setSeedRows(null); setFormKey((k) => k + 1); }} data-testid={`${testid}-prefill-clear`} title="Bersihkan prefill">✕</button>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              Kepala Cabang mengajukan <b>Merchandize + Size + Jenis Stok + Qty</b> (tanpa scan QR).
              Admin Gudang yang akan scan QR fisik sesuai request saat <b>Konfirmasi Kirim</b>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{sourceLabel}</Label>
                <Select value={source} onValueChange={setSource} disabled={!owner}>
                  <SelectTrigger className="mt-1" data-testid={`${testid}-source`}><SelectValue /></SelectTrigger>
                  <SelectContent>{sourceOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{targetLabel}</Label>
                <Select value={target} onValueChange={setTarget}>
                  <SelectTrigger className="mt-1" data-testid={`${testid}-target`}><SelectValue /></SelectTrigger>
                  <SelectContent>{validTargets.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Baris Pengajuan</Label>
              <div className="mt-1">
                <RequestLineEditor key={formKey} products={products} allowMou={allowMou} onLinesChange={setReqLines} initialRows={seedRows} testid={`${testid}-lines`} />
              </div>
            </div>
            <div className="flex items-center justify-between">
              <Badge variant="outline" data-testid={`${testid}-total`}>{totalReqQty} pcs</Badge>
              <Button onClick={create} disabled={busy} data-testid={`${testid}-submit`}>
                {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PlusCircle className="w-4 h-4 mr-1" />}{createLabel}
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-5 rounded-2xl grid place-items-center text-center text-sm text-muted-foreground min-h-[160px]" data-testid={`${testid}-readonly`}>
            {owner
              ? "Mode Owner: read-only (audit/monitoring)."
              : canDispatch
                ? "Sebagai Admin Gudang: scan QR untuk memenuhi & mengirim request pada daftar."
                : "Role Anda hanya dapat melihat / menerima dokumen pada modul ini."}
          </Card>
        )}

        <Card className="p-5 rounded-2xl" data-testid={`${testid}-info-card`}>
          <h3 className="font-semibold mb-2">Alur Request → Konfirmasi Kirim</h3>
          <ol className="text-sm text-muted-foreground space-y-2 list-decimal pl-4">
            <li>Kepala Cabang mengajukan request (Merchandize, Size, Jenis Stok, Qty) — tanpa QR.</li>
            <li>Admin Gudang scan QR fisik. Sistem memvalidasi <b>persis</b> sesuai request (kurang/lebih/beda = ditolak).</li>
            <li>Setelah cocok, Admin Gudang menekan <b>Konfirmasi Kirim</b> → stok keluar & surat jalan terbit.</li>
            <li>Penerima ({receiverLocation || "tujuan"}) <b>WAJIB scan QR fisik</b> saat <b>Terima</b>. Sistem mencocokkan persis dengan kiriman (kurang/lebih/beda/duplikat = ditolak).</li>
          </ol>
        </Card>
      </div>

      <Card className="p-5 rounded-2xl" ref={listTopRef}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">Daftar Dokumen</h3>
          <Button size="icon" variant="ghost" onClick={loadDocs} data-testid={`${testid}-refresh`}><RefreshCw className="w-4 h-4" /></Button>
        </div>
        <div className="hidden md:block overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>No</TableHead><TableHead>Rute</TableHead><TableHead>Request</TableHead>
              <TableHead>Status / Timeline</TableHead><TableHead className="text-right">Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {showSkeleton && docs.length === 0 && <TableSkeletonRows rows={4} cols={5} testid={`${testid}-skeleton`} />}
              {!showSkeleton && docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="p-0">
                    <EmptyState icon={Inbox} title={emptyT} description={emptyD} testid={`${testid}-empty`} />
                  </TableCell>
                </TableRow>
              )}
              {docs.map((d) => {
                const from = d.from_location || d.from_store;
                const to = d.to_location || d.to_store;
                return (
                  <TableRow key={d.id} data-testid={`${testid}-row`}
                    className="cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => setDetailDoc(d)}
                    tabIndex={0} role="button"
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDetailDoc(d); } }}>
                    <TableCell className="font-mono text-xs align-top">{d.no}</TableCell>
                    <TableCell className="text-sm align-top"><span className="inline-flex items-center gap-1">{from}<ArrowRight className="w-3 h-3 text-muted-foreground" />{to}</span></TableCell>
                    <TableCell className="align-top max-w-[260px]">
                      {(d.request_lines && d.request_lines.length)
                        ? <RequestLinesSummary lines={d.request_lines} testid={`${testid}-reqsum`} />
                        : <span className="text-[11px] text-muted-foreground">{d.planned_count} item (legacy)</span>}
                    </TableCell>
                    <TableCell className="align-top">
                      <StatusBadge status={d.status} label={STATUS_LABEL[d.status] || d.status} testid={`${testid}-status`} />
                      <div className="text-[10px] text-muted-foreground mt-1 space-y-0.5" data-testid={`${testid}-timeline`}>
                        {d.requested_at && <div>Diajukan: {fmt(d.requested_at)}</div>}
                        {!d.requested_at && d.created_at && <div>Dibuat: {fmt(d.created_at)}</div>}
                        {d.shipped_at && <div>Dikirim: {fmt(d.shipped_at)}</div>}
                        {d.received_at && <div>Diterima: {fmt(d.received_at)}</div>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap align-top" onClick={(e) => e.stopPropagation()}>
                      {renderActions(d)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: card list dokumen */}
        <div className="md:hidden space-y-3" data-testid={`${testid}-cards`}>
          {showSkeleton && docs.length === 0 && <ListSkeleton rows={3} testid={`${testid}-skeleton-m`} />}
          {!showSkeleton && docs.length === 0 && (
            <EmptyState icon={Inbox} title={emptyT} description={emptyD} testid={`${testid}-empty-m`} className="rounded-xl border border-dashed" />
          )}
          {docs.map((d) => {
            const from = d.from_location || d.from_store;
            const to = d.to_location || d.to_store;
            return (
              <div key={d.id} className="rounded-xl border p-3 space-y-2 cursor-pointer hover:bg-muted/50 transition-colors" data-testid={`${testid}-row-m`}
                onClick={() => setDetailDoc(d)}
                tabIndex={0} role="button"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); setDetailDoc(d); } }}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs">{d.no}</span>
                  <StatusBadge status={d.status} label={STATUS_LABEL[d.status] || d.status} />
                </div>
                <div className="text-sm inline-flex items-center gap-1">{from}<ArrowRight className="w-3 h-3 text-muted-foreground" />{to}</div>
                <div>
                  {(d.request_lines && d.request_lines.length)
                    ? <RequestLinesSummary lines={d.request_lines} testid={`${testid}-reqsum-m`} />
                    : <span className="text-[11px] text-muted-foreground">{d.planned_count} item (legacy)</span>}
                </div>
                <div className="text-[10px] text-muted-foreground space-y-0.5">
                  {d.requested_at && <div>Diajukan: {fmt(d.requested_at)}</div>}
                  {!d.requested_at && d.created_at && <div>Dibuat: {fmt(d.created_at)}</div>}
                  {d.shipped_at && <div>Dikirim: {fmt(d.shipped_at)}</div>}
                  {d.received_at && <div>Diterima: {fmt(d.received_at)}</div>}
                </div>
                <div className="flex flex-wrap gap-2 pt-1 border-t" onClick={(e) => e.stopPropagation()}>
                  {renderActions(d, "-m")}
                </div>
              </div>
            );
          })}
        </div>

        {(total > 0 || loadingDocs) && (
          <ListPager
            page={page} pageSize={pageSize} total={total} pages={pages} loading={loadingDocs}
            onPageChange={goPage} onPageSizeChange={changePageSize}
            storageKey={pagerKey} testid={`${testid}-pager`}
          />
        )}
      </Card>

      {/* Dispatch dialog: Admin Gudang scan QR sesuai request */}
      <Dialog open={!!dispatchDoc} onOpenChange={(o) => !o && setDispatchDoc(null)}>
        <DialogContent data-testid={`${testid}-dispatch-dialog`}>
          <DialogHeader><DialogTitle>Scan &amp; Kirim · {dispatchDoc?.no}</DialogTitle></DialogHeader>
          {dispatchDoc && (
            <div className="space-y-3 text-sm">
              <div>
                <Label className="text-xs">Target Request (harus dipenuhi PERSIS)</Label>
                <div className="mt-1"><RequestLinesSummary lines={dispatchDoc.request_lines || []} testid={`${testid}-dispatch-target`} /></div>
                <div className="text-xs text-muted-foreground mt-1">Total diminta: <b>{dispatchDoc.request_count}</b> pcs</div>
              </div>
              <div>
                <Label className="text-xs">Scan QR fisik</Label>
                <div className="mt-1"><QrBatchInput value={scanQrs} onChange={(v) => { setScanQrs(v); setSellPlanWarn(null); }} testid={`${testid}-dispatch-qr`} /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg bg-muted px-3 py-2">
                <span className="text-xs">Discan</span>
                <Badge variant={scanQrs.length === dispatchDoc.request_count ? "default" : "secondary"} data-testid={`${testid}-dispatch-count`}>
                  {scanQrs.length} / {dispatchDoc.request_count}
                </Badge>
              </div>
              {sellPlanWarn && sellPlanWarn.length > 0 && (
                <div className="rounded-xl border border-[hsl(var(--badge-warning))]/50 bg-[hsl(var(--badge-warning))]/10 px-3 py-2 space-y-1" data-testid={`${testid}-sellplan-warn`}>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-[hsl(var(--badge-warning))]" />
                    <span className="text-sm font-medium text-[hsl(var(--badge-warning))]">Perhatian: barang ditandai "tidak dijual" di store tujuan</span>
                    <Badge variant="warning" data-testid={`${testid}-sellplan-warn-count`}>{sellPlanWarn.length}</Badge>
                  </div>
                  <ul className="text-xs text-[hsl(var(--badge-warning))] list-disc pl-5">
                    {sellPlanWarn.map((w, i) => (
                      <li key={i} data-testid={`${testid}-sellplan-warn-item`}>
                        {w.product_name}{w.size ? ` · ${w.size}` : ""}
                      </li>
                    ))}
                  </ul>
                  <p className="text-[11px] text-[hsl(var(--badge-warning))]/80">Pengiriman TETAP boleh dilanjutkan — ini hanya pengingat.</p>
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Jika jumlah/merchandize/size/jenis stok tidak sesuai request, konfirmasi kirim akan ditolak (tanpa partial).
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDispatchDoc(null)}>Tutup</Button>
            <Button onClick={submitDispatch} disabled={actingId === dispatchDoc?.id + "dispatch"} data-testid={`${testid}-dispatch-submit`}>
              {actingId === dispatchDoc?.id + "dispatch" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Truck className="w-4 h-4 mr-1" />}
              {sellPlanWarn && sellPlanWarn.length > 0 ? "Lanjutkan Kirim" : "Konfirmasi Kirim"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* UAT-04 Receive dialog: penerima WAJIB scan QR fisik sesuai kiriman (exact-match) */}
      <Dialog open={!!receiveDoc} onOpenChange={(o) => !o && setReceiveDoc(null)}>
        <DialogContent data-testid={`${testid}-receive-dialog`}>
          <DialogHeader><DialogTitle>Scan &amp; Terima · {receiveDoc?.no}</DialogTitle></DialogHeader>
          {receiveDoc && (
            <div className="space-y-3 text-sm">
              {staged ? (
                <div className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
                  Penerimaan boleh <b>bertahap</b> dalam satu Surat Jalan. Scan QR barang yang sudah datang
                  lalu <b>Simpan Sebagian</b>. <b>Tanda Terima final hanya terbit jika lengkap 100%.</b>
                </div>
              ) : (
                <div className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
                  Scan <b>SEMUA</b> QR fisik barang yang Anda terima. Sistem mencocokkan <b>persis</b> dengan
                  yang dikirim (kurang / lebih / beda / duplikat = ditolak). Tidak boleh terima sebagian.
                </div>
              )}
              {staged && progress && (
                <ProgressPanel progress={progress} testid={`${testid}-receive-progress`} />
              )}
              <div>
                <Label className="text-xs">{staged ? "Scan QR barang yang baru datang" : "Scan QR barang diterima"}</Label>
                <div className="mt-1"><QrBatchInput value={receiveQrs} onChange={setReceiveQrs} testid={`${testid}-receive-qr`} /></div>
              </div>
              {!staged && (
                <div className="flex items-center justify-between rounded-lg bg-muted px-3 py-2">
                  <span className="text-xs">Discan</span>
                  <Badge variant={receiveQrs.length === (receiveDoc.planned_count || 0) ? "default" : "secondary"} data-testid={`${testid}-receive-count`}>
                    {receiveQrs.length} / {receiveDoc.planned_count || 0}
                  </Badge>
                </div>
              )}
              {!staged && receiveQrs.length !== (receiveDoc.planned_count || 0) && (
                <p className="text-[11px] text-[hsl(var(--badge-warning))]">
                  Jumlah QR discan ({receiveQrs.length}) belum sama dengan jumlah kiriman ({receiveDoc.planned_count || 0}).
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiveDoc(null)}>Tutup</Button>
            {staged && (
              <Button variant="secondary" onClick={saveProgress}
                disabled={actingId === receiveDoc?.id + "progress" || receiveQrs.length === 0}
                data-testid={`${testid}-receive-save-progress`}>
                {actingId === receiveDoc?.id + "progress" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ScanLine className="w-4 h-4 mr-1" />}Simpan Sebagian
              </Button>
            )}
            <Button
              onClick={submitReceive}
              disabled={
                actingId === receiveDoc?.id + "diterima" ||
                (staged
                  ? !((progress?.scanned || 0) + receiveQrs.length >= (progress?.dispatched_total || 0) && (progress?.dispatched_total || 0) > 0)
                  : (receiveQrs.length === 0 || receiveQrs.length !== (receiveDoc?.planned_count || 0)))
              }
              data-testid={`${testid}-receive-submit`}
            >
              {actingId === receiveDoc?.id + "diterima" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-1" />}{staged ? "Konfirmasi Terima Final" : "Konfirmasi Terima"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 9C.3 (GAP-1) Read-only: PENGIRIM (Admin Gudang) / Kacab / Owner melihat progres penerimaan */}
      <Dialog open={!!viewProgressDoc} onOpenChange={(o) => !o && setViewProgressDoc(null)}>
        <DialogContent data-testid={`${testid}-view-progress-dialog`}>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ListChecks className="w-5 h-5" />Progres Penerimaan · {viewProgressDoc?.no}</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
              Tampilan <b>hanya-baca</b> untuk memantau penerimaan Store. Barang yang <b>belum diterima</b> ditampilkan
              lengkap dengan nama, size, klub, dan kategori (MoU/Jual).
            </div>
            {viewProgress
              ? <ProgressPanel progress={viewProgress} testid={`${testid}-view-progress-panel`} />
              : <div className="text-xs text-muted-foreground py-4 text-center">Memuat progres…</div>}
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setViewProgressDoc(null)}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* C: Diagnostic Mode ringan — detail masalah QR (Pengiriman & Penerimaan) */}
      <Dialog open={!!errDetail} onOpenChange={(o) => !o && setErrDetail(null)}>
        <DialogContent data-testid={`${testid}-error-detail-dialog`}>
          <DialogHeader><DialogTitle className="text-destructive">Detail Masalah QR</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] overflow-auto rounded-lg bg-muted p-3">
            <pre className="text-xs whitespace-pre-wrap font-mono leading-relaxed" data-testid={`${testid}-error-detail-text`}>{errDetail}</pre>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setErrDetail(null)}>Tutup</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Tahap 3 UX: Detail dokumen read-only (klik baris/kartu). Aksi tetap tersedia di footer. */}
      <Dialog open={!!detailDoc} onOpenChange={(o) => !o && setDetailDoc(null)}>
        <DialogContent data-testid={`${testid}-detail-dialog`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="w-5 h-5" />Detail · {detailDoc?.no}
            </DialogTitle>
          </DialogHeader>
          {detailDoc && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Status</span>
                <StatusBadge status={detailDoc.status} label={STATUS_LABEL[detailDoc.status] || detailDoc.status} testid={`${testid}-detail-status`} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">Rute</span>
                <span className="inline-flex items-center gap-1 font-medium">
                  {detailDoc.from_location || detailDoc.from_store}
                  <ArrowRight className="w-3 h-3 text-muted-foreground" />
                  {detailDoc.to_location || detailDoc.to_store}
                </span>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Isi Pengajuan</div>
                {(detailDoc.request_lines && detailDoc.request_lines.length)
                  ? <RequestLinesSummary lines={detailDoc.request_lines} testid={`${testid}-detail-reqsum`} />
                  : <span className="text-xs text-muted-foreground">{detailDoc.planned_count} item (legacy)</span>}
              </div>
              <div className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground space-y-0.5" data-testid={`${testid}-detail-timeline`}>
                {detailDoc.requested_at && <div>Diajukan: {fmt(detailDoc.requested_at)}</div>}
                {!detailDoc.requested_at && detailDoc.created_at && <div>Dibuat: {fmt(detailDoc.created_at)}</div>}
                {detailDoc.shipped_at && <div>Dikirim: {fmt(detailDoc.shipped_at)}</div>}
                {detailDoc.received_at && <div>Diterima: {fmt(detailDoc.received_at)}</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDoc(null)}>Tutup</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
